import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiSettings, FiCheck, FiLock, FiFileText, FiDownload, FiDollarSign, FiUsers, FiXCircle, FiEdit2, FiUpload } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import logo from '../../../assets/logo.png';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import StatusPill from '../../components/ui/StatusPill';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import { Select, Input, NumberInput, Textarea } from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import PayslipDocument, { getDeductionTotal, getGrossTotal, getTakeHome } from './PayslipDocument';
import { 
  listPayrollForPeriod, getPayrollConfigs, createPayrollConfig, updatePayrollConfig,
  generatePayrollBulk, generatePayrollAll, updateDraftPayroll,
  lockPayroll, lockPayrollBulk, markPayrollPaid, markPayrollPaidBulk,
  uploadPayrollConfigsExcel
} from '../../api/payroll';
import { listActiveUsers } from '../../api/admin';
import toast from 'react-hot-toast';

function MetricCard({ title, value, icon: Icon, colorClass }) {
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
      <div className={`p-3 rounded-full ${colorClass} bg-opacity-10`}>
        <Icon className={`w-6 h-6 ${colorClass.replace('bg-', 'text-')}`} />
      </div>
      <div>
        <div className="text-sm text-slate-500 font-medium">{title}</div>
        <div className="text-xl font-bold text-slate-800">{value}</div>
      </div>
    </div>
  );
}

const SALARY_FIELDS = [
  'basicSalary', 'hra', 'transportAllowance', 'medicalAllowance', 'fixedAllowance',
  'specialAllowance', 'bonus', 'incentives', 'otherAllowances',
  'pfContribution', 'professionalTax', 'tds', 'esi', 'loanDeduction', 'otherDeductions',
  'paidDays', 'lopDays'
];

export default function PayrollAdminPage() {
  const queryClient = useQueryClient();
  const date = new Date();
  const [activeTab, setActiveTab] = useState('runs'); // 'runs' | 'config'
  const [month, setMonth] = useState(date.getMonth() + 1);
  const [year, setYear] = useState(date.getFullYear());
  
  // Modals state
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('ALL');
  const [printSlip, setPrintSlip] = useState(null);
  const [configModalUser, setConfigModalUser] = useState(null);
  const [draftEditData, setDraftEditData] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Forms
  const [configForm, setConfigForm] = useState({});
  const [draftForm, setDraftForm] = useState({});
  const [saveOverrides, setSaveOverrides] = useState(false);

  // Actions state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [rowActionId, setRowActionId] = useState(null);
  
  const fileInputRef = React.useRef(null);

  // Queries
  const { data: rawUsers } = useQuery({ queryKey: ['activeUsers'], queryFn: listActiveUsers });
  const { data: rawPayrolls, isLoading: loadingRuns, error: errRuns, refetch: refetchRuns } = useQuery({
    queryKey: ['payroll', month, year],
    queryFn: () => listPayrollForPeriod({ month, year }),
    enabled: activeTab === 'runs'
  });
  const { data: rawConfigs, isLoading: loadingConfigs } = useQuery({
    queryKey: ['payrollConfigs'],
    queryFn: getPayrollConfigs,
    enabled: activeTab === 'config'
  });

  const ensureArray = (raw) => Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.content) ? raw.content : []));
  
  const users = ensureArray(rawUsers);
  const payrolls = ensureArray(rawPayrolls);
  const configs = ensureArray(rawConfigs);

  const usersMap = useMemo(() => users.reduce((acc, u) => ({ ...acc, [u.id]: u }), {}), [users]);
  const configsMap = useMemo(() => configs.reduce((acc, c) => ({ ...acc, [c.userId]: c }), {}), [configs]);

  const totals = useMemo(() => {
    if (!payrolls) return { gross: 0, deductions: 0, net: 0, count: 0 };
    return payrolls.reduce((acc, p) => ({
      gross: acc.gross + getGrossTotal(p),
      deductions: acc.deductions + getDeductionTotal(p),
      net: acc.net + getTakeHome(p),
      count: acc.count + 1
    }), { gross: 0, deductions: 0, net: 0, count: 0 });
  }, [payrolls]);

  // Actions
  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadPayrollConfigsExcel(file);
      toast.success('Excel configs uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['payrollConfigs'] });
    } catch (err) {
      toast.error('Failed to upload Excel');
    } finally {
      setIsUploading(false);
      e.target.value = null; // reset
    }
  };

  const handleRunSubmit = async () => {
    setIsGenerating(true);
    try {
      if (selectedUserId === 'ALL') {
         await generatePayrollAll({ month, year });
         toast.success('Batch generation complete');
      } else {
         await generatePayrollBulk({ month, year, userIds: [Number(selectedUserId)] });
         toast.success('Payroll generated successfully');
      }
      queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
      setRunModalOpen(false);
    } catch (err) {
      toast.error('Failed to generate payroll');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBulkLock = async () => {
     const unlockables = payrolls.filter(p => !p.locked && !p.paid && p.status !== 'LOCKED' && p.status !== 'PAID');
     if (unlockables.length === 0) return toast.error('No generated payrolls to lock');
     setIsLocking(true);
     try {
       await lockPayrollBulk({ payrollIds: unlockables.map(p => p.id) });
       toast.success(`Locked ${unlockables.length} payrolls`);
       queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
     } catch (err) {
       toast.error(err?.message || 'Failed to lock some payrolls');
     }
     setIsLocking(false);
  };

  const handleBulkPay = async () => {
     const payables = payrolls.filter(p => (p.locked || p.status === 'LOCKED') && !p.paid && p.status !== 'PAID');
     if (payables.length === 0) return toast.error('No locked payrolls to pay');
     setIsPaying(true);
     try {
       await markPayrollPaidBulk({ payrollIds: payables.map(p => p.id) });
       toast.success(`Marked ${payables.length} payrolls as Paid`);
       queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
     } catch {
       toast.error('Failed to pay some payrolls');
     }
     setIsPaying(false);
  };

  const handleSingleLock = async (id) => {
    setRowActionId(`lock-${id}`);
    try {
      await lockPayroll(id);
      toast.success('Payroll locked');
      queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
    } catch (err) {
      toast.error(err?.message || 'Failed to lock');
    }
    setRowActionId(null);
  };

  const handleSinglePay = async (id) => {
    setRowActionId(`pay-${id}`);
    try {
      await markPayrollPaid(id);
      toast.success('Payroll marked as paid');
      queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
    } catch (err) {
      toast.error(err?.message || 'Failed to mark paid');
    }
    setRowActionId(null);
  };

  const openConfigModal = (u) => {
    setConfigModalUser(u);
    const existing = configsMap[u.id] || {};
    setConfigForm({ ...existing });
  };

  const handleConfigSubmit = async () => {
    setIsSaving(true);
    try {
      if (configsMap[configModalUser.id]) {
        await updatePayrollConfig(configModalUser.id, configForm);
      } else {
        await createPayrollConfig({ ...configForm, userId: configModalUser.id });
      }
      toast.success('Configuration saved');
      queryClient.invalidateQueries({ queryKey: ['payrollConfigs'] });
      setConfigModalUser(null);
    } catch (e) {
      toast.error('Failed to save configuration');
    }
    setIsSaving(false);
  };

  const openDraftEditModal = (p) => {
    setDraftEditData(p);
    setSaveOverrides(false);
    setDraftForm({ ...p });
  };

  const handleDraftSubmit = async () => {
    setIsSaving(true);
    try {
      await updateDraftPayroll(draftEditData.id, { ...draftForm, saveOverridesToConfig: saveOverrides });
      toast.success('Draft payroll updated');
      queryClient.invalidateQueries({ queryKey: ['payroll', month, year] });
      setDraftEditData(null);
    } catch (e) {
      toast.error('Failed to update draft payroll');
    }
    setIsSaving(false);
  };

  const handleDownloadPdf = async (payroll) => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const runColumns = [
    {
      key: 'userId',
      header: 'Employee',
      render: (r) => {
        const u = usersMap[r.userId];
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-slate-800">{r.employeeName || (u ? `${u.firstName} ${u.lastName}` : `User #${r.userId}`)}</span>
            <span className="text-xs text-slate-500">{r.employeeId || u?.employeeId || 'Unknown'}</span>
          </div>
        );
      },
    },
    {
      key: 'grossSalary',
      header: 'CTC / Gross',
      align: 'right',
      render: (r) => <CurrencyDisplay value={getGrossTotal(r)} className="font-medium text-slate-700" />,
    },
    {
      key: 'deductions',
      header: 'Deductions',
      align: 'right',
      render: (r) => <span className="text-rose-500"><CurrencyDisplay value={getDeductionTotal(r)} /></span>,
    },
    {
      key: 'netSalary',
      header: 'Take Home',
      align: 'right',
      render: (r) => <CurrencyDisplay value={getTakeHome(r)} className="font-bold text-emerald-700" />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const isPaid = r.paid || r.status === 'PAID';
        const isLocked = r.locked || r.status === 'LOCKED';
        const val = isPaid ? 'PAID' : isLocked ? 'LOCKED' : r.status || 'GENERATED';
        return <StatusPill value={val} />;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => {
        const isPaid = r.paid || r.status === 'PAID';
        const isLocked = r.locked || r.status === 'LOCKED';
        const isDraft = r.status === 'DRAFT' || (!isLocked && !isPaid);
        
        return (
        <div className="flex justify-end gap-2">
           <Button variant="outline" size="sm" icon={FiFileText} onClick={() => setPrintSlip(r)}>Slip</Button>
           {isDraft && (
             <Button variant="outline" size="sm" icon={FiEdit2} onClick={() => openDraftEditModal(r)}>Edit</Button>
           )}
          {isDraft && (
            <Button variant="outline" size="sm" icon={FiLock} loading={rowActionId === `lock-${r.id}`} disabled={rowActionId !== null} onClick={() => handleSingleLock(r.id)}>Lock</Button>
          )}
          {isLocked && !isPaid && (
            <Button variant="primary" size="sm" icon={FiCheck} loading={rowActionId === `pay-${r.id}`} disabled={rowActionId !== null} onClick={() => handleSinglePay(r.id)}>Pay</Button>
          )}
        </div>
      )},
    },
  ];

  const configColumns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (u) => (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-800">{u.firstName} {u.lastName}</span>
          <span className="text-xs text-slate-500">{u.employeeId}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Config Status',
      render: (u) => {
        const hasConfig = !!configsMap[u.id];
        return <StatusPill value={hasConfig ? 'CONFIGURED' : 'PENDING'} />;
      }
    },
    {
      key: 'baseSalary',
      header: 'Base Salary',
      align: 'right',
      render: (u) => <CurrencyDisplay value={configsMap[u.id]?.basicSalary || 0} className="font-medium text-slate-700" />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (u) => (
         <Button variant="outline" size="sm" icon={FiSettings} onClick={() => openConfigModal(u)}>
            {configsMap[u.id] ? 'Edit Config' : 'Setup Config'}
         </Button>
      ),
    }
  ];

  const monthName = new Date(0, month - 1).toLocaleString('default', { month: 'long' });

  return (
    <AppLayout title="Payroll Administration" breadcrumb="HR Operations / Manage Payroll">
      <div className="print-hidden">
        <PageHeader
          title="Payroll Administration"
          description="Configure defaults, generate, review, and finalize monthly employee payrolls."
          actions={
            <div className="flex gap-3">
              <Button variant="outline" icon={FiUpload} onClick={() => setUploadModalOpen(true)} loading={isUploading}>
                Upload Excel
              </Button>
            </div>
          }
        />

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6 flex gap-6 px-1">
           <button 
             className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'runs' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
             onClick={() => setActiveTab('runs')}
           >
             Monthly Payroll
           </button>
           <button 
             className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
             onClick={() => setActiveTab('config')}
           >
             Salary Setup
           </button>
        </div>

        {activeTab === 'runs' && (
          <div>
            {/* Filters & Bulk Actions */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
              <div className="flex gap-4">
                <Select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="sm:w-48 bg-slate-50">
                  {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
                  ))}
                </Select>
                <Select value={year} onChange={(e) => setYear(Number(e.target.value))} className="sm:w-32 bg-slate-50">
                  {[...Array(5)].map((_, i) => {
                    const y = new Date().getFullYear() - 2 + i;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </Select>
              </div>
              <div className="flex gap-2">
                 <Button variant="outline" icon={FiSettings} onClick={() => setRunModalOpen(true)}>Generate</Button>
                 <Button variant="outline" icon={FiLock} onClick={handleBulkLock} loading={isLocking}>Approve All</Button>
                 <Button variant="outline" icon={FiDollarSign} onClick={handleBulkPay} loading={isPaying}>Mark Paid All</Button>
              </div>
            </div>

            {/* Metrics Ribbon */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard title="Payslips Generated" value={totals.count} icon={FiUsers} colorClass="bg-blue-500" />
              <MetricCard title="Total CTC / Gross" value={`₹${totals.gross.toLocaleString('en-IN')}`} icon={FiDollarSign} colorClass="bg-slate-500" />
              <MetricCard title="Total Deductions" value={`₹${totals.deductions.toLocaleString('en-IN')}`} icon={FiXCircle} colorClass="bg-rose-500" />
              <MetricCard title="Total Take Home" value={`₹${totals.net.toLocaleString('en-IN')}`} icon={FiCheck} colorClass="bg-emerald-500" />
            </div>

            {errRuns && (
              <div className="mb-6">
                <ErrorBanner error={errRuns} title="Failed to load payroll data" onRetry={refetchRuns} />
              </div>
            )}

            {/* Roster Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <DataTable
                columns={runColumns}
                rows={payrolls}
                loading={loadingRuns}
                keyField="id"
                empty={
                  <EmptyState
                    icon={FiSettings}
                    title="No payroll records"
                    description={`No payroll has been generated for ${monthName} ${year}.`}
                    action={<Button icon={FiSettings} onClick={() => setRunModalOpen(true)} variant="outline">Generate Now</Button>}
                  />
                }
              />
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <DataTable
                columns={configColumns}
                rows={users}
                loading={loadingConfigs}
                keyField="id"
                empty={<EmptyState icon={FiUsers} title="No users found" description="No active users available to configure." />}
             />
          </div>
        )}
      </div>

      {/* Run Payroll Modal */}
      <Modal open={runModalOpen} onClose={() => setRunModalOpen(false)} title="Generate" description={`Generate payslips for ${monthName} ${year}`}>
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Employees</label>
            <Select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} className="w-full">
              <option value="ALL">All Active Employees (Batch Run)</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.employeeId})</option>
              ))}
            </Select>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start gap-3">
             <FiSettings className="w-5 h-5 text-blue-500 mt-0.5" />
             <div className="text-sm text-blue-800">
                This will calculate base salary, apply structured allowances and deductions, and generate a draft payslip based on the employee's configuration.
             </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setRunModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleRunSubmit} loading={isGenerating}>Generate</Button>
        </div>
      </Modal>

      {/* Config Form Modal */}
      <Modal open={!!configModalUser} onClose={() => setConfigModalUser(null)} title="Employee Payroll Configuration" size="lg">
         {configModalUser && (
           <div className="py-2">
             <div className="mb-4">
               <div className="text-sm font-medium text-slate-700">Configuring for:</div>
               <div className="text-lg font-bold text-slate-900">{configModalUser.firstName} {configModalUser.lastName}</div>
             </div>
             <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Personal Details hidden as per request */}
                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-2">Earnings</div>
                {['basicSalary', 'hra', 'transportAllowance', 'medicalAllowance', 'fixedAllowance', 'specialAllowance', 'bonus', 'incentives', 'otherAllowances'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={configForm[f] || ''} onChange={e => setConfigForm({...configForm, [f]: Number(e.target.value)})} />
                   </div>
                ))}
                
                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-2">Deductions</div>
                {['pfContribution', 'professionalTax', 'tds', 'esi', 'loanDeduction', 'otherDeductions'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={configForm[f] || ''} onChange={e => setConfigForm({...configForm, [f]: Number(e.target.value)})} />
                   </div>
                ))}

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-2">Days Default</div>
                <div>
                   <label className="block text-xs font-medium text-slate-700 mb-1">Paid Days (Max)</label>
                   <NumberInput value={configForm.paidDays || ''} onChange={e => setConfigForm({...configForm, paidDays: Number(e.target.value)})} />
                </div>
                <div>
                   <label className="block text-xs font-medium text-slate-700 mb-1">LOP Days (Default)</label>
                   <NumberInput value={configForm.lopDays || ''} onChange={e => setConfigForm({...configForm, lopDays: Number(e.target.value)})} />
                </div>

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-2">Notes</div>
                <div className="col-span-2">
                   <Textarea value={configForm.notes || ''} onChange={e => setConfigForm({...configForm, notes: e.target.value})} rows={2} />
                </div>
             </div>
             <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
               <Button variant="outline" onClick={() => setConfigModalUser(null)}>Cancel</Button>
               <Button variant="primary" onClick={handleConfigSubmit} loading={isSaving}>Save Configuration</Button>
             </div>
           </div>
         )}
      </Modal>

      {/* Edit Draft Form Modal */}
      <Modal open={!!draftEditData} onClose={() => setDraftEditData(null)} title="Edit Draft Payroll" size="lg">
         {draftEditData && (
           <div className="py-2">
             <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
                
                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-2">Gross Wages (Base)</div>
                {['grossBasic', 'grossDa', 'grossHra', 'grossConvAll', 'grossMedicalAllowance', 'grossOtherAllowances'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={draftForm[f] === undefined || draftForm[f] === null ? '' : draftForm[f]} onChange={e => setDraftForm({...draftForm, [f]: e.target.value ? Number(e.target.value) : null})} />
                   </div>
                ))}

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-4">Earnings Overrides (Actuals)</div>
                {['earnBasic', 'earnDa', 'earnHra', 'earnConvAll', 'earnMedicalAllowance', 'earnOtherAllowances'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={draftForm[f] === undefined || draftForm[f] === null ? '' : draftForm[f]} onChange={e => setDraftForm({...draftForm, [f]: e.target.value ? Number(e.target.value) : null})} />
                   </div>
                ))}
                
                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-4">Deductions & Contributions</div>
                {['esicEmployer', 'esicEmployee', 'tds', 'professionalTax'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={draftForm[f] === undefined || draftForm[f] === null ? '' : draftForm[f]} onChange={e => setDraftForm({...draftForm, [f]: e.target.value ? Number(e.target.value) : null})} />
                   </div>
                ))}

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-4">Additional Payments & Overrides</div>
                {['reimbursements', 'incentives', 'takeHomeSalary'].map(f => (
                   <div key={f}>
                     <label className="block text-xs font-medium text-slate-700 mb-1 capitalize">{f.replace(/([A-Z])/g, ' $1').trim()}</label>
                     <NumberInput value={draftForm[f] === undefined || draftForm[f] === null ? '' : draftForm[f]} onChange={e => setDraftForm({...draftForm, [f]: e.target.value ? Number(e.target.value) : null})} />
                   </div>
                ))}

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-4">Days</div>
                <div>
                   <label className="block text-xs font-medium text-slate-700 mb-1">Paid Days</label>
                   <NumberInput value={draftForm.daysPresent === undefined || draftForm.daysPresent === null ? '' : draftForm.daysPresent} onChange={e => setDraftForm({...draftForm, daysPresent: e.target.value ? Number(e.target.value) : null})} />
                </div>
                <div>
                   <label className="block text-xs font-medium text-slate-700 mb-1">LOP Days</label>
                   <NumberInput value={draftForm.lop === undefined || draftForm.lop === null ? '' : draftForm.lop} onChange={e => setDraftForm({...draftForm, lop: e.target.value ? Number(e.target.value) : null})} />
                </div>

                <div className="col-span-2 text-sm font-bold text-slate-800 border-b pb-1 mt-4">Notes</div>
                <div className="col-span-2">
                   <Textarea value={draftForm.notes || ''} onChange={e => setDraftForm({...draftForm, notes: e.target.value})} rows={2} />
                </div>

                <div className="col-span-2 mt-2 flex items-center gap-2">
                   <input type="checkbox" id="saveOverrides" checked={saveOverrides} onChange={e => setSaveOverrides(e.target.checked)} className="rounded border-slate-300 text-primary focus:ring-primary" />
                   <label htmlFor="saveOverrides" className="text-sm font-medium text-slate-700">Save overrides to employee's default configuration for next month</label>
                </div>
             </div>
             <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
               <Button variant="outline" onClick={() => setDraftEditData(null)}>Cancel</Button>
               <Button variant="primary" onClick={handleDraftSubmit} loading={isSaving}>Update Draft</Button>
             </div>
           </div>
         )}
      </Modal>

      {/* Upload Excel Modal */}
      <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} title="Upload Payroll Excel" size="lg">
         <div className="py-2">
            <div className="mb-4 text-sm text-slate-600">
               Upload the monthly payroll Excel sheet to automatically configure base salaries, earnings overrides, and deductions for all employees.
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6">
               <h4 className="font-bold text-slate-800 text-sm mb-2">Required Excel Column Pattern</h4>
               <p className="text-xs text-slate-500 mb-3">The system matches columns by header name (in Row 2, under the main group headers). Column order does not matter.</p>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-xs leading-relaxed">
                  <div><span className="font-semibold text-slate-700 block mb-0.5">Identity & Details:</span> EMP ID, NAME OF THE EMPLOYEE, DESIGNATION, PAN NUMBER, BANK ACCOUNT NO:, DOJ</div>
                  <div><span className="font-semibold text-slate-700 block mb-0.5">Gross Wages (Base):</span> TOTAL CTC, BASIC, DA, HRA, CONV. ALL, MEDICAL ALLOWANCE, OTHER ALLOWANCES, TOTAL GROSS SALARY</div>
                  <div><span className="font-semibold text-slate-700 block mb-0.5">Earnings Overrides:</span> The second occurrence of BASIC, DA, etc. will automatically map to actual earned values for the month.</div>
                  <div><span className="font-semibold text-slate-700 block mb-0.5">Deductions & Taxes:</span> ESIC @ 3.25%, ESIC @ 0.75%, TDS, PROFESSIONAL TAX</div>
                  <div className="md:col-span-2"><span className="font-semibold text-slate-700 block mb-0.5">Additional & Overrides:</span> REIMBURSEMENTS, INCENTIVES, TAKE HOME SALARY, DAYS PRESENT, LOP</div>
               </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
               <input 
                 type="file" 
                 ref={fileInputRef} 
                 className="hidden" 
                 accept=".xlsx,.xls" 
                 onChange={(e) => { setUploadModalOpen(false); handleUploadExcel(e); }} 
               />
               <Button variant="outline" onClick={() => setUploadModalOpen(false)}>Cancel</Button>
               <Button variant="primary" icon={FiUpload} onClick={() => fileInputRef.current?.click()} loading={isUploading}>
                 Select File & Upload
               </Button>
            </div>
         </div>
      </Modal>

      {/* Print Slip Modal */}
      <Modal open={!!printSlip} onClose={() => setPrintSlip(null)} title="Payslip Details" size="xl">
        {printSlip && (
          <div className="p-4" style={{ maxWidth: '1000px', margin: '0 auto', width: '100%', overflowX: 'auto' }}>
            <style>{`
              @page {
                size: A4 portrait;
                margin: 8mm;
              }
              @media print {
                html, body {
                  width: 210mm !important;
                  min-height: 297mm !important;
                  margin: 0 !important;
                  background: white !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                /* Hide all elements by default */
                body * {
                  visibility: hidden;
                }
                
                /* Ensure portal wrapper allows natural flow */
                body > div {
                  position: static !important;
                  height: auto !important;
                  max-height: none !important;
                  overflow: visible !important;
                }
                
                /* Reset modal layout constraints */
                .fixed.inset-0, 
                .relative.z-10, 
                .overflow-y-auto {
                  position: static !important;
                  height: auto !important;
                  max-height: none !important;
                  overflow: visible !important;
                  transform: none !important;
                  box-shadow: none !important;
                  background: transparent !important;
                }
                
                /* Make payslip visible */
                #printable-payslip, #printable-payslip * {
                  visibility: visible;
                }
                
                /* Position payslip normally so it spans pages */
                #printable-payslip {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 194mm !important;
                  min-width: 194mm !important;
                  max-width: 194mm !important;
                  box-sizing: border-box !important;
                  margin: 0 !important;
                  padding: 12mm 14mm 8mm !important;
                  background: white !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                
                .no-print {
                  display: none !important;
                }
              }
            `}</style>

            <PayslipDocument payroll={printSlip} user={usersMap[printSlip.userId]} monthName={monthName} />
            
            <div className="hidden">
              
              {/* Header */}
              <div className="flex justify-between items-end border-b border-slate-300 pb-5 mb-6">
                <div className="flex items-center gap-4">
                  <img src={logo} alt="Orakris Logo" className="h-10 object-contain" />
                  <div>
                    <h1 className="text-[22px] font-bold text-slate-800 leading-tight">{printSlip.companyName || "Orakris Med Solutions Pvt Ltd"}</h1>
                    <p className="text-slate-500 text-[13px] mt-0.5 leading-snug whitespace-pre-wrap">{printSlip.address || "123 Business Avenue, Tech Park\\nChennai Tamil Nadu 600001 India"}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-slate-500 text-xs mb-1">Payslip For the Month</div>
                  <div className="text-[15px] font-bold text-slate-800">{monthName} {printSlip.year}</div>
                </div>
              </div>

              {/* Employee Summary & Green Box */}
              <div className="flex justify-between mb-8">
                <div className="w-[55%] pr-4">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Employee Summary</div>
                  <table className="text-[13px] text-slate-700 w-full leading-[1.8]">
                    <tbody>
                      <tr><td className="w-36 text-slate-500 font-medium">Employee Name</td><td className="w-4 text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.employeeName || (usersMap[printSlip.userId]?.firstName + ' ' + usersMap[printSlip.userId]?.lastName)}</td></tr>
                      <tr><td className="text-slate-500 font-medium">Designation</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.designation || usersMap[printSlip.userId]?.jobTitle || 'N/A'}</td></tr>
                      <tr><td className="text-slate-500 font-medium">Employee ID</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.employeeId || usersMap[printSlip.userId]?.employeeId || 'N/A'}</td></tr>
                      <tr><td className="text-slate-500 font-medium">PAN Number</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.panNumber || usersMap[printSlip.userId]?.panNumber || 'N/A'}</td></tr>
                      <tr><td className="text-slate-500 font-medium">Date of Joining</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.dateOfJoining ? new Date(printSlip.dateOfJoining).toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata' }) : (usersMap[printSlip.userId]?.hireDate ? new Date(usersMap[printSlip.userId].hireDate).toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata' }) : 'N/A')}</td></tr>
                      <tr><td className="text-slate-500 font-medium">Pay Period</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{monthName} {printSlip.year}</td></tr>
                      <tr><td className="text-slate-500 font-medium">Bank Account No</td><td className="text-slate-400">:</td><td className="font-semibold text-slate-800">{printSlip.bankAccountNumber || 'N/A'}</td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="w-[320px]">
                  <div className="bg-[#f0fdf4] border border-[#dcfce7] rounded-xl p-5">
                    <div className="text-3xl font-bold text-slate-800 tracking-tight"><CurrencyDisplay value={getTakeHome(printSlip)} /></div>
                    <div className="text-[13px] text-[#16a34a] font-medium mt-1.5 mb-5">Total Take Home Pay</div>
                    <div className="border-t border-dashed border-[#bbf7d0] pt-4">
                      <table className="text-[13px] text-slate-600 w-full">
                        <tbody>
                          <tr><td className="pb-1.5 font-medium">Paid Days</td><td className="w-4 text-center">:</td><td className="font-bold text-slate-800 text-right">{printSlip.daysPresent || printSlip.paidDays || 0}</td></tr>
                          <tr><td className="font-medium">LOP Days</td><td className="w-4 text-center">:</td><td className="font-bold text-slate-800 text-right">{printSlip.lop || printSlip.lopDays || 0}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-dashed border-slate-300 my-4"></div>

              {/* Account Details */}
              <div className="flex justify-between text-[13px] text-slate-600 mb-6 px-1">
                <div className="flex w-[55%]"><div className="w-36 text-slate-500 font-medium">Bank Account No</div><div className="w-4 text-slate-400">:</div><div className="font-semibold text-slate-800">{printSlip.bankAccountNumber || 'N/A'}</div></div>
                <div className="flex w-[320px]"><div className="w-20 text-slate-500 font-medium">PAN No</div><div className="w-4 text-slate-400">:</div><div className="font-semibold text-slate-800">{printSlip.panNumber || 'N/A'}</div></div>
              </div>

              {/* Earnings & Deductions Table */}
              <div className="border border-slate-200 rounded-lg mb-6 flex flex-col">
                <div className="flex bg-white text-[10px] font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200">
                  <div className="w-1/2 flex px-4 py-3"><div className="flex-1">Earnings & Additions</div><div className="w-24 text-right">Amount</div></div>
                  <div className="w-1/2 flex px-4 py-3 border-l border-slate-200"><div className="flex-1">Deductions & Contributions</div><div className="w-24 text-right">Amount</div></div>
                </div>
                
                <div className="flex bg-white text-[13px] text-slate-700 min-h-[180px]">
                  {/* Left Column - Earnings (Gross) */}
                  <div className="w-1/2 space-y-0">
                    <div className="px-4 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">Gross Earnings</div>
                    <div className="px-4 py-3 space-y-3">
                      {(printSlip.grossBasic || printSlip.basicSalary || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Basic</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.grossBasic || printSlip.basicSalary} /></span></div>}
                      {(printSlip.grossDa || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">DA</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.grossDa} /></span></div>}
                      {(printSlip.grossHra || printSlip.hra || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="flex-1 font-medium text-slate-600">HRA</span>
                          <span className="w-24 text-right font-bold text-slate-800">
                            <CurrencyDisplay value={printSlip.grossHra || printSlip.hra} />
                          </span>
                        </div>
                      )}
                      {(printSlip.grossConvAll || printSlip.transportAllowance || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Conv. All</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.grossConvAll || printSlip.transportAllowance} /></span></div>}
                      {(printSlip.grossMedicalAllowance || printSlip.medicalAllowance || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Medical Allowance</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.grossMedicalAllowance || printSlip.medicalAllowance} /></span></div>}
                      {(printSlip.grossOtherAllowances || printSlip.otherAllowances || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Other Allowances</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.grossOtherAllowances || printSlip.otherAllowances} /></span></div>}
                      <div className="flex justify-between pt-2 border-t border-slate-100 mt-2"><span className="flex-1 font-bold text-slate-700">Gross Total</span><span className="w-24 text-right font-bold text-slate-800 text-[14px]"><CurrencyDisplay value={getGrossTotal(printSlip)} /></span></div>
                    </div>

                    <div className="px-4 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-y border-slate-100 mt-2">Additional Payments</div>
                    <div className="px-4 py-3 space-y-3">
                      {(printSlip.reimbursements || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Reimbursements</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.reimbursements} /></span></div>}
                      {(printSlip.incentives || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Incentives</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.incentives} /></span></div>}
                    </div>
                  </div>

                  {/* Right Column - Deductions & Contributions */}
                  <div className="w-1/2 space-y-0 border-l border-slate-200">
                    <div className="px-4 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">Employee Deductions</div>
                    <div className="px-4 py-3 space-y-3">
                      {(printSlip.esicEmployee || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">ESIC (Employee) @ 0.75%</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.esicEmployee} /></span></div>}
                      {(printSlip.tds || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">TDS</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.tds} /></span></div>}
                      {(printSlip.professionalTax || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">Professional Tax</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.professionalTax} /></span></div>}
                      <div className="flex justify-between pt-2 border-t border-slate-100 mt-2"><span className="flex-1 font-bold text-slate-700">Total Deductions</span><span className="w-24 text-right font-bold text-slate-800 text-[14px]"><CurrencyDisplay value={getDeductionTotal(printSlip)} /></span></div>
                    </div>

                    <div className="px-4 py-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-y border-slate-100 mt-2">Employer Contribution</div>
                    <div className="px-4 py-3 space-y-3">
                      {(printSlip.esicEmployer || 0) > 0 && <div className="flex justify-between"><span className="flex-1 font-medium text-slate-600">ESIC (Employer) @ 3.25%</span><span className="w-24 text-right font-bold text-slate-800"><CurrencyDisplay value={printSlip.esicEmployer} /></span></div>}
                      {(printSlip.esicEmployer || 0) === 0 && <div className="text-slate-500 text-sm py-2">No employer contributions</div>}
                    </div>
                  </div>
                </div>

                <div className="flex bg-slate-50/50 text-[13px] font-bold text-slate-800 border-t border-slate-200">
                  <div className="w-1/2 px-4 py-3 flex justify-between"><span className="flex-1">Gross (Earnings + Additional)</span><span className="w-24 text-right"><CurrencyDisplay value={getGrossTotal(printSlip) + (printSlip.reimbursements || 0) + (printSlip.incentives || 0)} /></span></div>
                  <div className="w-1/2 px-4 py-3 flex justify-between border-l border-slate-200"><span className="flex-1">Total Deductions</span><span className="w-24 text-right"><CurrencyDisplay value={getDeductionTotal(printSlip)} /></span></div>
                </div>
              </div>

              {/* Total Net Payable Box */}
              <div className="border border-slate-200 bg-[#f0fdf4] rounded-lg flex items-stretch mb-8 overflow-hidden h-14">
                <div className="flex-1 px-4 bg-white flex flex-col justify-center">
                  <div className="text-[12px] font-bold text-slate-800 uppercase tracking-wider mb-0.5">Total Net Payable</div>
                  <div className="text-[11px] text-slate-500 font-medium">After All Deductions & Contributions</div>
                </div>
                <div className="px-6 flex items-center justify-center border-l border-slate-200">
                  <div className="text-[15px] font-bold text-slate-800"><CurrencyDisplay value={getTakeHome(printSlip)} /></div>
                </div>
              </div>

              {/* Amount in words */}
              <div className="text-center text-[12px] text-slate-500 mb-16">
                Amount In Words : <span className="font-semibold text-slate-800">{printSlip.amountInWords || "Amount"}</span>
              </div>

              {/* Footer */}
              <div className="text-center text-[11px] text-slate-400 font-medium pb-4">
                -- This is a system-generated document. --
              </div>

            </div>

            <div className="mt-8 flex justify-end gap-3 no-print">
               <Button variant="outline" onClick={() => setPrintSlip(null)}>Close</Button>
               <Button
                 variant="primary"
                 icon={FiDownload}
                 loading={isPdfLoading}
                 onClick={() => handleDownloadPdf(printSlip)}
               >
                 Download PDF
               </Button>
            </div>
          </div>
        )}
      </Modal>

    </AppLayout>
  );
}
